import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionDivider from './SectionDivider';
import SectionHeader from './SectionHeader';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function ContentAccordionSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'full-width';

    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const mediaProps = { fitClass, interval: carouselInterval, accent: s.accent };

    const rawItems = content.items || [];
    const items = rawItems.map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));
    const [openIndex, setOpenIndex] = useState(0);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const borderColor = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

    // Custom active/inactive item colors
    const activeBg = config.active_bg || hexToRgba(s.accent, 0.04);
    const activeBorder = config.active_border || s.accent;
    const activeText = config.active_text || s.text;
    const activeDesc = config.active_desc || s.textSub;
    const activeIcon = config.active_icon || s.accent;
    const inactiveBg = config.inactive_bg || (s.dark ? 'rgba(255,255,255,0.03)' : '#ffffff');
    const inactiveBorder = config.inactive_border || borderColor;
    const inactiveText = config.inactive_text || s.text;

    const ICONS = {
        star: <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />,
        check: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
        bolt: <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />,
        cog: <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />,
        heart: <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />,
        shield: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
    };

    const getIcon = (iconName) => {
        if (!iconName || !ICONS[iconName]) return null;
        return (
            <svg className="w-5 h-5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                {ICONS[iconName]}
            </svg>
        );
    };

    const toggleIndex = (i) => setOpenIndex(openIndex === i ? null : i);

    const FeaturesList = ({ features = [] }) => {
        if (!features || features.length === 0) return null;
        const feats = Array.isArray(features) ? features : (typeof features === 'string' ? features.split('\n').filter(Boolean) : []);
        if (feats.length === 0) return null;
        return (
            <div className="mt-3 space-y-1.5">
                {feats.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                        <svg className="w-4 h-4 shrink-0" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span style={{ color: s.text }}>{f}</span>
                    </div>
                ))}
            </div>
        );
    };

    const AccordionHeader = ({ item, index, isOpen, prefix }) => (
        <button onClick={() => toggleIndex(index)} className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors">
            {prefix}
            {item.icon && getIcon(item.icon)}
            <span className="flex-1 text-sm font-semibold" style={{ color: isOpen ? activeText : inactiveText }}>{item.title}</span>
            <svg className={`w-5 h-5 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                style={{ color: isOpen ? activeIcon : s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
        </button>
    );

    // Shared content panel for full-width & numbered
    const AccordionContent = ({ item }) => (
        <div className="px-5 pb-5">
            {item.description && <p className="text-sm leading-relaxed mb-3" style={{ color: activeDesc }}>{item.description}</p>}
            <MediaRenderer item={item} {...mediaProps} aspect="aspect-video" rounded="rounded-xl" className="mb-3" />
            <FeaturesList features={item.features} />
            {item.cta_text && (
                <div className="mt-4">
                    <a {...resolveLinkProps(item.cta_link || '#')}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105 hover:shadow-lg"
                        style={{ backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }}>
                        {item.cta_text}
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </a>
                </div>
            )}
        </div>
    );

    // Shared item wrapper for all variants
    const ItemCard = ({ item, i, children, extraClass = '' }) => {
        const isOpen = openIndex === i;
        return (
            <div className={`rounded-xl border transition-all duration-300 ${extraClass} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ borderColor: isOpen ? activeBorder : inactiveBorder, backgroundColor: isOpen ? activeBg : inactiveBg, transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                {children}
            </div>
        );
    };

    // ── Split variant ──
    if (variant === 'split') {
        const activeItem = openIndex !== null ? items[openIndex] : items[0];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <div className="space-y-3">
                            {items.map((item, i) => {
                                const isOpen = openIndex === i;
                                return (
                                    <ItemCard key={i} item={item} i={i}>
                                        <AccordionHeader item={item} index={i} isOpen={isOpen} />
                                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                            <div className="px-5 pb-4">
                                                {item.description && <p className="text-sm leading-relaxed" style={{ color: activeDesc }}>{item.description}</p>}
                                                <FeaturesList features={item.features} />
                                                {item.cta_text && (
                                                    <div className="mt-3">
                                                        <a {...resolveLinkProps(item.cta_link || '#')} className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-80" style={{ color: s.accent }}>
                                                            {item.cta_text}
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </ItemCard>
                                );
                            })}
                        </div>
                        <div className="lg:sticky lg:top-24 rounded-2xl overflow-hidden transition-all duration-500">
                            <MediaRenderer item={activeItem || items[0]} {...mediaProps} />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Numbered variant ──
    if (variant === 'numbered') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-3">
                        {items.map((item, i) => {
                            const isOpen = openIndex === i;
                            return (
                                <ItemCard key={i} item={item} i={i} extraClass={card}>
                                    <AccordionHeader item={item} index={i} isOpen={isOpen}
                                        prefix={<span className="text-2xl font-black shrink-0 w-10 leading-none" style={{ color: hexToRgba(s.accent, 0.2) }}>{String(i + 1).padStart(2, '0')}</span>} />
                                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <AccordionContent item={item} />
                                    </div>
                                </ItemCard>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Full-width variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="space-y-3">
                    {items.map((item, i) => {
                        const isOpen = openIndex === i;
                        return (
                            <ItemCard key={i} item={item} i={i}>
                                <AccordionHeader item={item} index={i} isOpen={isOpen} />
                                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <AccordionContent item={item} />
                                </div>
                            </ItemCard>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
